import { Position, Node } from "@xyflow/react";
import dagre from "@dagrejs/dagre";
import RemovableEdge from "./edges/removable-edge";
import DeviceNode from "./nodes/device";
import DistributionNode from "./nodes/distribution";
import ZoneNode from "./nodes/zone";
import SourceNode from "./nodes/source";

export enum OrgChartNodeType {
  SOURCE = "source",
  DISTRIBUTION = "distribution",
  ZONE = "zone",
  DEVICE = "device",
}

export const nodeTypes = {
  deviceNode: DeviceNode,
  distributionNode: DistributionNode,
  zoneNode: ZoneNode,
  sourceNode: SourceNode,
};

export const edgeTypes = {
  "removable-edge": RemovableEdge,
};

export interface DeviceNodeDataInfo {
  usage: number;
  childrenUsage: number;
  parentUsage: number;
  info: DeviceNodeDataInfo[];
}

export interface DeviceNodeDevice {
  snr: string;
  name: string;
  model?: string;
  classId?: string;
  timezone?: number;
}

export class SourceNodeData {
  id: string;
  targets: string[];
  name: string;
  type: OrgChartNodeType = OrgChartNodeType.SOURCE;
  data?: DeviceNodeDataInfo;
  device?: DeviceNodeDevice;
  width?: number;
  height?: number;
  sourcePosition?: Position;
  onDelete?: (id: string) => void;

  constructor(
    id: string,
    targets: string[],
    name: string,
    type: OrgChartNodeType = OrgChartNodeType.SOURCE,
    device?: DeviceNodeDevice,
    onDelete?: (id: string) => void,
    data?: DeviceNodeDataInfo
  ) {
    this.onDelete = onDelete;
    this.id = id;
    this.targets = targets;
    this.type = type;
    this.name = name;
    this.device = device;
    this.data = data;
  }
}

export class DistributionNodeData {
  id: string;
  targets: string[];
  name: string;
  type: OrgChartNodeType = OrgChartNodeType.DISTRIBUTION;
  width?: number;
  height?: number;
  sourcePosition?: Position;
  targetPosition?: Position;
  onDelete?: (id: string) => void;

  constructor(
    id: string,
    targets: string[],
    name: string,
    type: OrgChartNodeType = OrgChartNodeType.DISTRIBUTION,
    onDelete?: (id: string) => void
  ) {
    this.onDelete = onDelete;
    this.id = id;
    this.targets = targets;
    this.type = type;
    this.name = name;
  }
}

export class DeviceNodeData {
  id: string;
  targets: string[];
  name: string;
  type: OrgChartNodeType = OrgChartNodeType.DEVICE;
  data?: DeviceNodeDataInfo;
  device?: DeviceNodeDevice;
  width?: number;
  height?: number;
  targetPosition?: Position;
  sourcePosition?: Position;
  onDelete?: (id: string) => void;

  constructor(
    id: string,
    name: string,
    type: OrgChartNodeType = OrgChartNodeType.DEVICE,
    device?: DeviceNodeDevice,
    onDelete?: (id: string) => void,
    targets: string[] = [],
    data?: DeviceNodeDataInfo
  ) {
    this.onDelete = onDelete;
    this.id = id;
    this.targets = targets;
    this.type = type;
    this.name = name;
    this.device = device;
    this.data = data;
  }
}

export interface ZoneLeafNode {
  label: string;
  device: DeviceNodeDevice;
  data?: DeviceNodeDataInfo;
}

export class ZoneNodeData {
  id: string;
  targets: string[];
  name: string;
  type: OrgChartNodeType = OrgChartNodeType.ZONE;
  data?: DeviceNodeDataInfo;
  leafNodes: ZoneLeafNode[];
  width?: number;
  height?: number;
  sourcePosition?: Position;
  targetPosition?: Position;
  onDelete?: (id: string) => void;
  onRemoveDevice?: (zoneId: string, deviceSnr: string) => void;

  constructor(
    id: string,
    targets: string[],
    name: string,
    type: OrgChartNodeType = OrgChartNodeType.ZONE,
    leafNodes: ZoneLeafNode[] = [],
    width?: number,
    height?: number,
    onDelete?: (id: string) => void,
    onRemoveDevice?: (zoneId: string, deviceSnr: string) => void,
    data?: DeviceNodeDataInfo
  ) {
    this.id = id;
    this.targets = targets;
    this.name = name;
    this.type = type;
    this.leafNodes = leafNodes;
    this.width = width;
    this.height = height;
    this.onDelete = onDelete;
    this.onRemoveDevice = onRemoveDevice;
    this.data = data;
  }
}

export class OrgChartEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  animated: boolean;

  data?: { onDelete?: (source: string, target: string) => void };
}

export function getOrgLayout(
  allNodes: Array<
    SourceNodeData | DeviceNodeData | DistributionNodeData | ZoneNodeData
  >,
  direction: "TB" | "LR" = "TB",
  nodeSize = { width: 350, height: 150 },
  distributionNodeSize = { width: 40, height: 40 },
  distributionLayoutSize = { width: 50, height: 50 },
  clusterSize = { width: 350, height: 500 },
  onDeleteNode?: (id: string) => void,
  onDeleteEdge?: (source: string, target: string) => void,
  onDeleteZone?: (zoneId: string) => void
) {
  const currentEdges: OrgChartEdge[] = [];

  // Source nodes are origins only - they have outputs but never receive edges
  const sourceNodeIds = new Set(
    allNodes
      .filter((n) => "type" in n && n.type === OrgChartNodeType.SOURCE)
      .map((n) => n.id)
  );

  // Create edges for each current node (source → target)
  allNodes.forEach((node) => {
    if ("targets" in node) {
      node.targets.forEach((target) => {
        // Never create edges pointing TO a source node - sources are origins only
        if (sourceNodeIds.has(target)) return;

        currentEdges.push({
          id: `e_${node.id}_${target}`,
          source: node.id,
          target: target,
          animated: true,
          type: "removable-edge",
          data: {
            onDelete: onDeleteEdge
              ? () => onDeleteEdge(node.id, target)
              : undefined,
          },
        });
      });
    }
  });

  //#region : Dagre layout

  const isHorizontal = direction === "LR";
  const dagreGraph = new dagre.graphlib.Graph({
    directed: true,
  }).setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: direction, // TB, LR, RL, BT
    ranksep: direction === "LR" ? 100 : 200,
    nodesep: direction === "LR" ? 50 : 80,
    align: direction === "LR" ? "UL" : "DL", // UL, UR, DL, DR
    // acyclicer: "greedy",
    // ranker: "longest-path",
  });

  const getLayoutSize = (node: (typeof allNodes)[number]) => {
    if ("type" in node && node.type === OrgChartNodeType.DISTRIBUTION)
      return distributionLayoutSize;
    if ("type" in node && node.type === OrgChartNodeType.ZONE)
      return clusterSize;
    if ("type" in node && node.type === OrgChartNodeType.DEVICE)
      return nodeSize;
    if ("targets" in node) return nodeSize;
    return clusterSize;
  };

  const getDisplaySize = (node: (typeof allNodes)[number]) => {
    if ("type" in node && node.type === OrgChartNodeType.DISTRIBUTION)
      return distributionNodeSize;
    if ("type" in node && node.type === OrgChartNodeType.ZONE)
      return clusterSize;
    if ("type" in node && node.type === OrgChartNodeType.DEVICE)
      return nodeSize;
    if ("targets" in node) return nodeSize;
    return clusterSize;
  };

  allNodes.forEach((node) => {
    const size = getLayoutSize(node);
    dagreGraph.setNode(node.id, {
      width: size.width,
      height: size.height,
    });
  });

  currentEdges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph, { disableOptimalOrderHeuristic: false });

  // Post-process: align nodes that share the same target on the same line
  // (TB: same Y / horizontal line; LR: same X / vertical line)
  const targetToSources = new Map<string, string[]>();
  currentEdges.forEach((edge) => {
    const sources = targetToSources.get(edge.target) ?? [];
    if (!sources.includes(edge.source)) sources.push(edge.source);
    targetToSources.set(edge.target, sources);
  });

  targetToSources.forEach((sourceIds) => {
    if (sourceIds.length < 2) return;

    const positions = sourceIds.map((id) => dagreGraph.node(id));
    const alignCoord = isHorizontal ? "x" : "y";
    const avgCoord =
      positions.reduce((sum, p) => sum + (p[alignCoord] ?? 0), 0) /
      positions.length;

    sourceIds.forEach((id) => {
      const pos = dagreGraph.node(id);
      dagreGraph.setNode(id, { ...pos, [alignCoord]: avgCoord });
    });
  });

  //#endregion

  //#region : Apply layout to nodes

  const getReactFlowNodeType = (node: (typeof allNodes)[number]): string => {
    if ("type" in node) {
      switch (node.type) {
        case OrgChartNodeType.SOURCE:
          return "sourceNode";
        case OrgChartNodeType.DEVICE:
          return "deviceNode";
        case OrgChartNodeType.DISTRIBUTION:
          return "distributionNode";
        case OrgChartNodeType.ZONE:
          return "zoneNode";
      }
    }
    return "deviceNode";
  };

  const currentNodes: Node[] = allNodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    const displaySize = getDisplaySize(node);

    return {
      id: node.id,
      type: getReactFlowNodeType(node),
      position: {
        x: nodeWithPosition.x - displaySize.width / 2,
        y: nodeWithPosition.y - displaySize.height / 2,
      },
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      data: {
        ...node,
        label: node.name,
        width: displaySize.width,
        height: displaySize.height,
        sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
        targetPosition: isHorizontal ? Position.Left : Position.Top,
        onDelete:
          "type" in node && node.type === OrgChartNodeType.ZONE && onDeleteZone
            ? () => onDeleteZone(node.id)
            : onDeleteNode
            ? () => onDeleteNode(node.id)
            : undefined,
      },
    };
  });

  //#endregion

  return {
    nodes: currentNodes,
    edges: currentEdges,
  };
}
